import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Loader, ArrowLeft, Sparkles, Star } from "lucide-react";
import { AvatarDisplay } from "./ParentDashboard";

export default function KidsLoginPage() {
  const navigate = useNavigate();
  const { token, login } = useAuth();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (token) {
      loadChildren();
    } else {
      setShowLogin(true);
      setLoading(false);
    }
  }, [token]);

  const loadChildren = async () => {
    try {
      const { data } = await axios.get(`${API}/children`);
      setChildren(data);
      if (data.length === 0) {
        setShowLogin(true);
      }
    } catch (e) {
      console.error(e);
      setShowLogin(true);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      login(data.token, data.user);
      const childrenRes = await axios.get(`${API}/children`, {
        headers: { Authorization: `Bearer ${data.token}` }
      });
      setChildren(childrenRes.data);
      setShowLogin(false);
      if (childrenRes.data.length === 0) {
        setError("No kids found! Ask your parent to add you.");
        setShowLogin(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Wrong email or password. Ask your parent for help!");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSelectChild = (child) => {
    navigate(`/child/${child.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #FFD166 0%, #06D6A0 50%, #118AB2 100%)" }}>
        <Loader size={48} className="animate-spin" color="white" />
      </div>
    );
  }

  // Kids Login Form
  if (showLogin && children.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4"
        style={{ background: "linear-gradient(135deg, #FFD166 0%, #06D6A0 50%, #118AB2 100%)" }}>
        
        {/* Top bar */}
        <div className="absolute top-4 left-4">
          <button onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-white/80 hover:text-white font-bold text-sm">
            <ArrowLeft size={16} /> Back
          </button>
        </div>

        <div className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl">
          {/* Fun Header */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="bg-gradient-to-br from-[#FFD166] to-[#06D6A0] rounded-full p-4 shadow-lg">
                <Star size={40} color="white" fill="white" />
              </div>
            </div>
            <h1 className="text-3xl font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
              Hi There!
            </h1>
            <p className="text-gray-500 font-semibold mt-1">
              Ask your parent for their login
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">
                Parent's Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#06D6A0] transition-colors text-gray-800 font-medium text-lg"
                placeholder="parent@email.com"
                required
                data-testid="kids-email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:border-[#06D6A0] transition-colors text-gray-800 font-medium text-lg"
                placeholder="••••••"
                required
                data-testid="kids-password-input"
              />
            </div>

            {error && (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl px-4 py-3">
                <p className="text-red-600 text-sm font-medium">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loginLoading}
              className="w-full font-black text-xl py-4 rounded-2xl border-b-4 active:border-b-0 active:translate-y-1 transition-all disabled:opacity-60"
              style={{ 
                fontFamily: "Fredoka, sans-serif", 
                background: "linear-gradient(90deg, #06D6A0, #118AB2)", 
                borderColor: "#049a73", 
                color: "white" 
              }}
              data-testid="kids-login-btn"
            >
              {loginLoading ? "Loading..." : "Let's Go!"}
            </button>
          </form>

          <p className="text-center text-gray-400 text-xs mt-6">
            Parents can add kids in their dashboard
          </p>
        </div>

        {/* Fun decorations */}
        <div className="fixed bottom-4 left-4 w-16 h-16 rounded-full bg-white/20" />
        <div className="fixed top-20 right-8 w-12 h-12 rounded-full bg-white/20" />
      </div>
    );
  }

  // Profile Selection Screen
  return (
    <div className="min-h-screen px-4 py-8"
      style={{ background: "linear-gradient(135deg, #FFD166 0%, #06D6A0 50%, #118AB2 100%)" }}>
      
      {/* Back to home */}
      <button onClick={() => navigate("/")}
        className="flex items-center gap-1.5 text-white/80 hover:text-white font-bold text-sm mb-6">
        <ArrowLeft size={16} /> Back to Home
      </button>

      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-4xl md:text-5xl font-black text-white mb-3"
          style={{ fontFamily: "Fredoka, sans-serif", textShadow: "0 4px 15px rgba(0,0,0,0.2)" }}>
          Who's learning today?
        </h1>
        <p className="text-white/80 font-semibold text-lg">
          Tap your picture to start!
        </p>
      </div>

      {/* Kids Grid */}
      <div className="max-w-3xl mx-auto">
        <div className={`grid gap-6 ${children.length === 1 ? 'grid-cols-1 max-w-xs mx-auto' : children.length === 2 ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-3'}`}>
          {children.map((child) => (
            <button
              key={child.id}
              onClick={() => handleSelectChild(child)}
              className="bg-white rounded-3xl p-6 shadow-2xl hover:scale-105 transition-all duration-200 active:scale-95"
              data-testid={`kid-select-${child.id}`}
            >
              <div className="flex flex-col items-center">
                <div className="mb-4 relative">
                  <AvatarDisplay avatar={child.avatar} size={100} />
                  <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#06D6A0] flex items-center justify-center shadow-lg">
                    <Sparkles size={16} color="white" />
                  </div>
                </div>
                <h2 className="text-2xl font-black text-[#073B4C] mb-1"
                  style={{ fontFamily: "Fredoka, sans-serif" }}>
                  {child.name}
                </h2>
                <p className="text-sm font-semibold text-gray-400">
                  {child.age_group === "little" ? "Little Light" : child.age_group === "big" ? "Bible Explorer" : "Faith Builder"}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Fun decorations */}
      <div className="fixed bottom-4 left-4 w-16 h-16 rounded-full bg-white/20" />
      <div className="fixed top-20 right-8 w-12 h-12 rounded-full bg-white/20" />
      <div className="fixed bottom-20 right-12 w-8 h-8 rounded-full bg-white/30" />
    </div>
  );
}
